import { useApiClient } from "@/src/hooks/useApiClient";

/**
 * Les catégories de jeu, résolues une fois pour toute la session.
 *
 * ## Pourquoi une requête plutôt qu'une table écrite ici
 *
 * Une publication ne porte que son `categoryId`. Recopier les noms dans l'admin
 * donnerait une table qui diverge à la première catégorie ajoutée côté serveur
 * — c'est déjà l'argument retenu pour les noms d'armées des événements, que le
 * serveur résout et que l'admin n'essaie pas de deviner.
 *
 * ⚠️ **`staleTime: Infinity`.** La liste ne bouge qu'à l'ajout d'un jeu, soit
 * quelques fois par an ; sans ça chaque carte de la file relancerait la requête
 * à chaque montage. Une seule requête par session, partagée par le cache.
 */
export function useCategories() {
  const $api = useApiClient();

  const { data } = $api.useQuery(
    "get",
    "/api/v1/category",
    {},
    { staleTime: Infinity, gcTime: Infinity },
  );

  return data?.data ?? [];
}

/** Le nom d'une catégorie, ou `null` tant qu'on ne le connaît pas. */
export function useCategoryName(categoryId: number | null): string | null {
  const categories = useCategories();

  if (categoryId === null) return null;

  return (
    categories.find((category) => category.id === categoryId)?.name ?? null
  );
}
